import React, { Fragment, useState } from "react";
import { closeModal, openModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form, Field } from "formik";
import ErrorBoundary from "../../containers/ErrorBoundary";
import SubmitButton from "../Forms/SubmitButton";
import * as Yup from "yup";
import { toast } from "react-toastify";

const validationSchema = Yup.object().shape({
  amount: Yup.number()
    .required("Amount is required")
    .positive("Amount must be positive")
    .min(0.01, "Amount must be at least 0.01"),
  refund_reason: Yup.string()
    .required("Reason is required")
    .max(1000, "Reason must be less than 1000 characters"),
  refund_group: Yup.string()
    .required("Refund group is required")
    .oneOf(["Sarel", "CI Refund", "Cash Back", "Cash Back 2025"], "Invalid refund group"),
});

export default function ModalEditRefund(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const { permissions } = useSelector((state) => state.auth);
  const { payment, onSuccess } = props;
  
  const canSeeSarel = permissions?.includes('receive_refund_otp');

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}payments/batch/pending/${payment.id}`,
        {
          amount: values.amount,
          refund_reason: values.refund_reason,
          refund_group: values.refund_group,
        }
      );

      if (response.data.success) {
        toast.success("Refund updated successfully");
        dispatch(closeModal());
        
        // Reopen the batch queue modal
        setTimeout(() => {
          dispatch(openModal({
            modal_type: "ModalBatchRefunds",
            modal_props: {}
          }));
        }, 100);
      } else {
        toast.error(response.data.message || "Failed to update refund");
      }
    } catch (error) {
      console.error("Error updating refund:", error);
      toast.error(
        error.response?.data?.message || "Failed to update refund"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <ErrorBoundary>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                    onClick={() => dispatch(closeModal())}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faTimes}
                    />
                  </button>
                </div>

                <div className="pr-8 w-full">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h1"
                      className="text-lg font-medium text-gray-900 mb-4"
                    >
                      Edit Queued Refund
                    </Dialog.Title>

                    <div className="mb-4 p-3 bg-gray-50 rounded">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Booking:</span> #{payment.booking_reference} - {payment.tc_name}
                      </p>
                    </div>

                    <Formik
                      initialValues={{
                        amount: payment.amount,
                        refund_reason: payment.refund_reason,
                        refund_group: payment.refund_group || "",
                      }}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ errors, touched, isSubmitting }) => (
                        <Form>
                          <div className="space-y-4">
                            {/* Refund Group */}
                            <div>
                              <label
                                htmlFor="refund_group"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Refund Group *
                              </label>
                              <Field
                                as="select"
                                name="refund_group"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              >
                                <option value="">Select Group</option>
                                {canSeeSarel && <option value="Sarel">Sarel</option>}
                                <option value="CI Refund">CI Refund</option>
                                <option value="Cash Back">Cash Back</option>
                                <option value="Cash Back 2025">Cash Back 2025</option>
                              </Field>
                              {errors.refund_group && touched.refund_group && (
                                <div className="text-red-600 text-sm mt-1">
                                  {errors.refund_group}
                                </div>
                              )}
                            </div>

                            {/* Amount */}
                            <div>
                              <label
                                htmlFor="amount"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Amount (R)
                              </label>
                              <Field
                                type="number"
                                name="amount"
                                step="0.01"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="0.00"
                              />
                              {errors.amount && touched.amount && (
                                <div className="text-red-600 text-sm mt-1">
                                  {errors.amount}
                                </div>
                              )}
                            </div>

                            {/* Reason */}
                            <div>
                              <label
                                htmlFor="refund_reason"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Reason
                              </label>
                              <Field
                                as="textarea"
                                name="refund_reason"
                                rows="4"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="Enter reason for refund..."
                              />
                              {errors.refund_reason && touched.refund_reason && (
                                <div className="text-red-600 text-sm mt-1">
                                  {errors.refund_reason}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Buttons */}
                          <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                            <SubmitButton
                              text="Update"
                              isSubmitting={isSubmitting}
                              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm"
                            />
                            <button
                              type="button"
                              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                              onClick={() => dispatch(closeModal())}
                            >
                              Cancel
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
